// Success and failure type definitions for role extraction.
type Success<T> = {
  data: T;
  error: null;
};

type Failure<E> = {
  data: null;
  error: E;
};

export type ExtractRoleIdResult<T, E = Error> = Success<T> | Failure<E>;

// Interface definitions for user and role structures.
interface Role {
  id: string;
  name: string;
  authLevel?: string | null;
  deleted?: boolean;
  rank?: number;
  isDefault?: boolean;
}

interface RoleWrapper {
  role: Role;
}

export interface User {
  id?: string;
  username?: string;
  email?: string;
  roles?: RoleWrapper[];
  [key: string]: any; // Allow for additional properties
}

const safelyExtractUserRoleId = (
  user?: User | null
): ExtractRoleIdResult<string> => {
  try {
    // Handle null/undefined user.
    if (!user) {
      throw new Error('User object is null or undefined');
    }

    // Check if roles array exists and has items.
    if (!user.roles || !Array.isArray(user.roles) || user.roles.length === 0) {
      throw new Error('User has no roles defined');
    }

    // Find first valid role ID.
    for (const roleObj of user.roles) {
      if (roleObj && roleObj.role && roleObj.role.id) {
        return { data: roleObj.role.name, error: null };
      }
    }

    // If no valid role ID found, throw error.
    throw new Error('No valid role ID found in user roles');
  } catch (error) {
    const extractionError: Error =
      error instanceof Error
        ? error
        : new Error('Unknown error during role extraction');

    return {
      data: null,
      error: extractionError,
    };
  }
};

export { safelyExtractUserRoleId };
